import {
  Lightbulb,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const RefinedAnalysis = ({ refinedData, questions, answers }) => {
  if (!refinedData || refinedData.error) {
    return null;
  }

  const getConfidenceBadge = (level) => {
    const styles = {
      high: "bg-red-100 text-red-800 border border-red-200",
      medium: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      low: "bg-green-100 text-green-800 border border-green-200",
    };

    const labels = {
      high: "High Confidence",
      medium: "Medium Confidence",
      low: "Low Confidence",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          styles[level] || styles.medium
        }`}
      >
        {labels[level] || level}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: "bg-red-100 text-red-800 border border-red-200",
      routine: "bg-blue-100 text-blue-800 border border-blue-200",
    };

    const labels = {
      urgent: "Urgent",
      routine: "Routine",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          styles[priority] || styles.routine
        }`}
      >
        {labels[priority] || priority}
      </span>
    );
  };

  // Create a summary of what the doctor answered
  const getAnswerSummary = () => {
    const answeredQuestions = questions.filter(
      (q) => answers[q.id] && answers[q.id] !== ""
    );
    return answeredQuestions.map((q) => ({
      question: q.question,
      answer: answers[q.id],
    }));
  };

  const answerSummary = getAnswerSummary();

  return (
    <div className="space-y-6">
      {/* Header with answered questions summary */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Refined Assessment
            </h3>
            <p className="text-sm text-gray-600">
              Updated analysis based on your responses
            </p>
          </div>
        </div>

        {answerSummary.length > 0 && (
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Your Responses:</h4>
            <div className="space-y-2">
              {answerSummary.map((item, index) => (
                <div key={index} className="flex items-start space-x-2 text-sm">
                  <ArrowRight className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-700">
                      {item.question}
                    </span>
                    <span className="text-gray-600 ml-2">→ {item.answer}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Refined Insights */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Lightbulb className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Key Insights
            </h3>
            <p className="text-sm text-gray-600">
              Based on your additional information
            </p>
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <p className="text-gray-700">{refinedData.refinedInsights}</p>
        </div>

        {refinedData.keyInsights && refinedData.keyInsights.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">Additional Insights:</h4>
            <ul className="space-y-1">
              {refinedData.keyInsights.map((insight, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Updated Differential Diagnosis */}
      {refinedData.updatedDifferentialDiagnosis &&
        refinedData.updatedDifferentialDiagnosis.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Updated Diagnosis
                </h3>
                <p className="text-sm text-gray-600">
                  Revised diagnostic considerations
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {refinedData.updatedDifferentialDiagnosis.map(
                (diagnosis, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900">
                        {diagnosis.condition}
                      </h4>
                      {getConfidenceBadge(diagnosis.confidence)}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {diagnosis.changeFromOriginal}
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        )}

      {/* Specific Recommendations */}
      {refinedData.specificRecommendations &&
        refinedData.specificRecommendations.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Specific Recommendations
                </h3>
                <p className="text-sm text-gray-600">
                  Targeted actions based on your responses
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {refinedData.specificRecommendations.map(
                (recommendation, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900">
                        {recommendation.action}
                      </h4>
                      {getPriorityBadge(recommendation.priority)}
                    </div>
                    <p className="text-sm text-gray-600">
                      {recommendation.reasoning}
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default RefinedAnalysis;
